'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Loader2,
  Check,
  Copy,
  ExternalLink,
  Instagram,
  RefreshCw,
  AlertTriangle,
  PauseCircle,
  PlayCircle,
  X,
  Plus,
  Tag,
  Info,
} from 'lucide-react'
import { toast } from 'sonner'
import { AddressFormFields } from '@/components/address-form-fields'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MoneyInput } from '@/components/ui/money-input'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Link } from '@tanstack/react-router'

import {
  useUpdateCreator,
  useCommissionTiers,
  useEnrichCreatorInstagram,
  useCreatorPrograms,
  useSyncCreatorCoupon,
  useLinkCreatorCoupon,
  useDeactivateCreatorCoupon,
} from '@/hooks'
import type { CreatorWithTier } from '@/lib/api/modules/creators'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const compact = (n: number) => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(n)

const STATUS_LABELS: Record<string, string> = {
  prospecting: 'Prospecção',
  negotiating: 'Negociação',
  active: 'Ativo',
  paused: 'Pausado',
  churned: 'Churn',
}

const STATUS_COLORS: Record<string, string> = {
  prospecting: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  negotiating: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  paused: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  churned: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
}

// ─── Save Button ─────────────────────────────────────────────────────────────

type SaveState = 'idle' | 'saving' | 'success'

function SectionSaveButton({ isDirty, saveState }: { isDirty: boolean; saveState: SaveState }) {
  return (
    <Button
      type="submit"
      size="sm"
      disabled={!isDirty}
      loading={saveState === 'saving'}
      className={[
        'h-8 min-w-[88px] text-xs font-medium transition-all duration-200',
        saveState === 'success'
          ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400'
          : '',
      ].join(' ')}
    >
      {saveState === 'success' && <Check className="mr-1.5 h-3.5 w-3.5" />}
      {saveState === 'saving' ? 'Salvando…' : saveState === 'success' ? 'Salvo!' : 'Salvar'}
    </Button>
  )
}

// ─── Section Wrapper ──────────────────────────────────────────────────────────

function SettingsSection({
  id,
  title,
  description,
  children,
  onSubmit,
  isDirty,
  saveState,
}: {
  id?: string
  title: string
  description?: string
  children: React.ReactNode
  onSubmit: (e: React.FormEvent) => void
  isDirty: boolean
  saveState: SaveState
}) {
  return (
    <Card id={id} className="shadow-none">
      <form onSubmit={onSubmit}>
        <CardHeader className="flex flex-row items-start justify-between gap-4 px-5 pb-3 pt-5">
          <div className="space-y-0.5">
            <CardTitle className="text-sm font-semibold">{title}</CardTitle>
            {description && <CardDescription className="text-xs">{description}</CardDescription>}
          </div>
          <SectionSaveButton isDirty={isDirty} saveState={saveState} />
        </CardHeader>
        <Separator />
        <CardContent className="space-y-4 px-5 py-5">{children}</CardContent>
      </form>
    </Card>
  )
}

// ─── useSectionSave hook ──────────────────────────────────────────────────────
// Encapsulates the save/success/idle state cycle per section.

function useSectionSave() {
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const triggerSave = useCallback(async (fn: () => Promise<void>) => {
    setSaveState('saving')
    try {
      await fn()
      setSaveState('success')
      timerRef.current = setTimeout(() => setSaveState('idle'), 2000)
    } catch {
      setSaveState('idle')
    }
  }, [])

  return { saveState, triggerSave }
}

// ─── Inline copy button ───────────────────────────────────────────────────────

function InlineCopyButton({ value, label = 'Copiar' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(value)
    toast.success('Copiado!')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`h-8 w-8 shrink-0 transition-colors ${
              copied ? 'text-emerald-500' : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={handleCopy}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copied ? 'Copiado!' : label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// ─── Schemas ──────────────────────────────────────────────────────────────────

const personalSchema = z.object({
  name: z.string().min(2, 'Mínimo 2 caracteres'),
  email: z.string().email('E-mail inválido'),
  phone: z.string().optional(),
  cpf: z.string().optional(),
  birthday: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato esperado: YYYY-MM-DD')
    .optional()
    .or(z.literal('')),
  gender: z
    .enum(['masculino', 'feminino', 'outro', 'prefiro_nao_dizer'])
    .optional()
    .or(z.literal('')),
  type: z.enum(['creator', 'ugc_creator', 'internal']),
  niche: z.string().optional(),
})

const contractSchema = z.object({
  contractType: z.enum(['commission', 'barter', 'fixed', 'fixed_plus_commission']),
  commissionTierId: z.string().optional(),
  tierOverride: z.boolean(),
  fixedValue: z.number().nullable().optional(),
})

const socialSchema = z.object({
  instagramUsername: z.string().optional(),
  tiktokUsername: z.string().optional(),
})

const couponSchema = z.object({
  couponCode: z
    .string()
    .optional()
    .refine((v) => !v || (v.length >= 3 && v.length <= 30 && /^[A-Z0-9\-_]+$/.test(v)), {
      message: 'Código inválido: use maiúsculas, números, - ou _. Mín 3, máx 30 caracteres.',
    }),
  programId: z.string().optional().nullable(),
})

const pixKeyTypePlaceholders: Record<string, string> = {
  cpf: '000.000.000-00',
  cnpj: '00.000.000/0000-00',
  email: 'pagamento@email.com',
  phone: '+5511999999999',
  random: 'Chave aleatória (UUID)',
}

const pixSchema = z.object({
  pixKeyType: z.enum(['cpf', 'cnpj', 'email', 'phone', 'random']).optional().nullable(),
  pixKey: z.string().optional(),
})

const addressSchema = z.object({
  zipCode: z.string().optional(),
  street: z.string().optional(),
  number: z.string().optional(),
  complement: z.string().optional(),
  neighborhood: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
})

// ─── Main Component ───────────────────────────────────────────────────────────

interface CreatorSettingsTabProps {
  creator: CreatorWithTier
}

export function CreatorSettingsTab({ creator }: CreatorSettingsTabProps) {
  const { mutateAsync: updateCreator } = useUpdateCreator()
  const { data: tiersRaw } = useCommissionTiers()
  const { mutate: enrichInstagram, isPending: isEnrichingInstagram } = useEnrichCreatorInstagram()

  // Commission tiers — unwrap API wrapper if needed
  const tiers = ((tiersRaw as { data?: unknown[] })?.data ?? tiersRaw ?? []) as Array<{
    id: string
    name: string
    commissionRate: number
    monthlySeedings: number
    minSalesCount: number
  }>

  const publicPageUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/creator/${creator.publicToken}`
      : `/creator/${creator.publicToken}`

  // ── Section A: Informações Pessoais ─────────────────────────────────────

  const personalForm = useForm<z.infer<typeof personalSchema>>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(personalSchema) as any,
    defaultValues: {
      name: creator.name ?? '',
      email: creator.email ?? '',
      phone: creator.phone ?? '',
      cpf: creator.cpf ?? '',
      birthday: creator.birthday ?? '',
      gender: (creator.gender as z.infer<typeof personalSchema>['gender']) ?? '',
      type: creator.type ?? 'creator',
      niche: creator.niche ?? '',
    },
    mode: 'onChange',
  })

  const { saveState: personalSave, triggerSave: triggerPersonalSave } = useSectionSave()

  const onSavePersonal = personalForm.handleSubmit(async (values) => {
    await triggerPersonalSave(async () => {
      await updateCreator({
        id: creator.id,
        data: {
          name: values.name,
          email: values.email,
          phone: values.phone || undefined,
          cpf: values.cpf || undefined,
          birthday: values.birthday || undefined,
          gender: values.gender || undefined,
          type: values.type,
          niche: values.niche || undefined,
        },
      })
      personalForm.reset(values)
    })
  })

  // ── Section B: Contrato & Comissão ───────────────────────────────────────

  const contractForm = useForm<z.infer<typeof contractSchema>>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(contractSchema) as any,
    defaultValues: {
      contractType: creator.contractType ?? 'commission',
      commissionTierId: creator.commissionTierId ?? undefined,
      tierOverride: creator.tierOverride ?? false,
      fixedValue: creator.fixedValue ?? null,
    },
    mode: 'onChange',
  })

  const contractType = useWatch({ control: contractForm.control, name: 'contractType' })
  const showFixedValue = contractType === 'fixed' || contractType === 'fixed_plus_commission'
  const hasCommission = contractType === 'commission' || contractType === 'fixed_plus_commission'

  const { saveState: contractSave, triggerSave: triggerContractSave } = useSectionSave()

  const onSaveContract = contractForm.handleSubmit(async (values) => {
    await triggerContractSave(async () => {
      await updateCreator({
        id: creator.id,
        data: {
          contractType: values.contractType,
          commissionTierId: values.tierOverride ? values.commissionTierId || undefined : undefined,
          tierOverride: values.tierOverride,
          fixedValue: showFixedValue ? (values.fixedValue ?? undefined) : undefined,
        },
      })
      contractForm.reset(values)
    })
  })

  // ── Section C: Redes Sociais ─────────────────────────────────────────────

  const socialForm = useForm<z.infer<typeof socialSchema>>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(socialSchema) as any,
    defaultValues: {
      instagramUsername: creator.instagramUsername ?? '',
      tiktokUsername: creator.tiktokUsername ?? '',
    },
    mode: 'onChange',
  })

  const { saveState: socialSave, triggerSave: triggerSocialSave } = useSectionSave()

  const onSaveSocial = socialForm.handleSubmit(async (values) => {
    await triggerSocialSave(async () => {
      await updateCreator({
        id: creator.id,
        data: {
          instagramUsername: values.instagramUsername || undefined,
          tiktokUsername: values.tiktokUsername || undefined,
        },
      })
      socialForm.reset(values)
    })
  })

  // ── Section D: Cupom & Links ─────────────────────────────────────────────

  const couponForm = useForm<z.infer<typeof couponSchema>>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(couponSchema) as any,
    defaultValues: {
      couponCode: creator.couponCode ?? '',
      programId: creator.programId ?? '',
    },
    mode: 'onChange',
  })

  const { saveState: couponSave, triggerSave: triggerCouponSave } = useSectionSave()
  const { data: programs } = useCreatorPrograms()
  const syncCoupon = useSyncCreatorCoupon()
  const linkCoupon = useLinkCreatorCoupon()
  const deactivateCoupon = useDeactivateCreatorCoupon()

  const onSaveCoupon = couponForm.handleSubmit(async (values) => {
    await triggerCouponSave(async () => {
      await updateCreator({
        id: creator.id,
        data: {
          couponCode: values.couponCode || null,
          programId: values.programId || null,
        },
      })
      couponForm.reset(values)
    })
  })

  // ── Section E: Pagamento PIX ─────────────────────────────────────────────

  const pixForm = useForm<z.infer<typeof pixSchema>>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(pixSchema) as any,
    defaultValues: {
      pixKeyType: (creator.pixKeyType as z.infer<typeof pixSchema>['pixKeyType']) ?? null,
      pixKey: creator.pixKey ?? '',
    },
    mode: 'onChange',
  })

  const pixKeyType = useWatch({ control: pixForm.control, name: 'pixKeyType' })
  const { saveState: pixSave, triggerSave: triggerPixSave } = useSectionSave()

  const onSavePix = pixForm.handleSubmit(async (values) => {
    await triggerPixSave(async () => {
      await updateCreator({
        id: creator.id,
        data: {
          pixKeyType: values.pixKeyType ?? undefined,
          pixKey: values.pixKey || undefined,
        },
      })
      pixForm.reset(values)
    })
  })

  // ── Section F: Endereço ──────────────────────────────────────────────────

  const addressForm = useForm<z.infer<typeof addressSchema>>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(addressSchema) as any,
    defaultValues: {
      zipCode: creator.address?.zipCode ?? '',
      street: creator.address?.street ?? '',
      number: creator.address?.number ?? '',
      complement: creator.address?.complement ?? '',
      neighborhood: creator.address?.neighborhood ?? '',
      city: creator.address?.city ?? '',
      state: creator.address?.state ?? '',
    },
    mode: 'onChange',
  })

  const { saveState: addressSave, triggerSave: triggerAddressSave } = useSectionSave()

  const onSaveAddress = addressForm.handleSubmit(async (values) => {
    await triggerAddressSave(async () => {
      await updateCreator({
        id: creator.id,
        data: {
          address: {
            zipCode: values.zipCode || undefined,
            street: values.street || undefined,
            number: values.number || undefined,
            complement: values.complement || undefined,
            neighborhood: values.neighborhood || undefined,
            city: values.city || undefined,
            state: values.state || undefined,
          },
        },
      })
      addressForm.reset(values)
    })
  })

  // ── Section G: Tags ──────────────────────────────────────────────────────

  const [tags, setTags] = useState<string[]>(creator.tags ?? [])
  const [tagInput, setTagInput] = useState('')
  const [isSavingTags, setIsSavingTags] = useState(false)

  const persistTags = useCallback(
    async (nextTags: string[]) => {
      setIsSavingTags(true)
      try {
        await updateCreator({ id: creator.id, data: { tags: nextTags } })
      } catch {
        // toast is shown by the hook
      } finally {
        setIsSavingTags(false)
      }
    },
    [creator.id, updateCreator]
  )

  const handleAddTag = async () => {
    const value = tagInput.trim().toLowerCase()
    if (!value || tags.includes(value)) {
      setTagInput('')
      return
    }
    const next = [...tags, value]
    setTags(next)
    setTagInput('')
    await persistTags(next)
  }

  const handleRemoveTag = async (tag: string) => {
    const next = tags.filter((t) => t !== tag)
    setTags(next)
    await persistTags(next)
  }

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    }
  }

  // ── Section I: Zona de Perigo ────────────────────────────────────────────

  const [isDangerPending, setIsDangerPending] = useState(false)

  const isActive = creator.status === 'active'

  const handleToggleStatus = async () => {
    setIsDangerPending(true)
    try {
      await updateCreator({
        id: creator.id,
        data: { status: isActive ? 'paused' : 'active' },
      })
    } catch {
      // handled by hook
    } finally {
      setIsDangerPending(false)
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* ── A: Informações Pessoais ──────────────────────────────────── */}
      <Form {...personalForm}>
        <SettingsSection
          title="Informações Pessoais"
          description="Nome, e-mail e dados de identificação do creator"
          onSubmit={onSavePersonal}
          isDirty={personalForm.formState.isDirty}
          saveState={personalSave}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={personalForm.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Nome <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Nome completo ou artístico" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={personalForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    E-mail <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="contato@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={personalForm.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <Input placeholder="+55 11 9 0000-0000" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={personalForm.control}
              name="cpf"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CPF</FormLabel>
                  <FormControl>
                    <Input placeholder="000.000.000-00" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={personalForm.control}
              name="birthday"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data de Nascimento</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={personalForm.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Gênero</FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione…" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="feminino">Feminino</SelectItem>
                      <SelectItem value="masculino">Masculino</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                      <SelectItem value="prefiro_nao_dizer">Prefiro não dizer</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={personalForm.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione…" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="creator">Creator</SelectItem>
                      <SelectItem value="ugc_creator">UGC Creator</SelectItem>
                      <SelectItem value="internal">Interno</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={personalForm.control}
              name="niche"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nicho</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Beleza, Fitness, Lifestyle" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </SettingsSection>
      </Form>

      {/* ── B: Contrato & Comissão ───────────────────────────────────── */}
      <Form {...contractForm}>
        <SettingsSection
          title="Contrato & Comissão"
          description="Modelo de contrato, tier de comissão e valor fixo"
          onSubmit={onSaveContract}
          isDirty={contractForm.formState.isDirty}
          saveState={contractSave}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={contractForm.control}
              name="contractType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Modelo de Contrato</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione…" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="commission">Apenas Comissão</SelectItem>
                      <SelectItem value="barter">Permuta</SelectItem>
                      <SelectItem value="fixed">Fixo Mensal</SelectItem>
                      <SelectItem value="fixed_plus_commission">Fixo + Comissão</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {hasCommission && (
              <FormField
                control={contractForm.control}
                name="commissionTierId"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>Tier de Comissão</FormLabel>
                      <Link
                        to="/settings/creators"
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Ver regras de tiers →
                      </Link>
                    </div>
                    <Select
                      value={field.value ?? ''}
                      onValueChange={(v) => field.onChange(v || undefined)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Automático (por desempenho)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {tiers.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name} — {(t.commissionRate * 100).toFixed(0)}% · {t.minSalesCount}+
                            vendas · {t.monthlySeedings} seed/mês
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          {/* Banner contextual por tipo de contrato */}
          {contractType === 'barter' && (
            <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950">
              <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertDescription className="text-sm text-amber-800 dark:text-amber-300">
                Creator recebe <strong>produtos (seedings)</strong>. Vendas rastreadas pelo cupom
                medem ROI, mas não geram obrigação de pagamento.
              </AlertDescription>
            </Alert>
          )}
          {contractType === 'fixed' && (
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-sm text-blue-800 dark:text-blue-300">
                Creator recebe um <strong>valor fixo mensal</strong>. Cupom é opcional e serve
                apenas para rastrear vendas — não altera o pagamento.
              </AlertDescription>
            </Alert>
          )}
          {contractType === 'commission' && (
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-sm text-blue-800 dark:text-blue-300">
                Creator recebe <strong>% por venda</strong> via cupom. O tier define a taxa e
                progride automaticamente por volume.
              </AlertDescription>
            </Alert>
          )}
          {contractType === 'fixed_plus_commission' && (
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-sm text-blue-800 dark:text-blue-300">
                Creator recebe <strong>valor fixo mensal + % por venda</strong>. O tier define a
                taxa variável.
              </AlertDescription>
            </Alert>
          )}

          {/* tierOverride switch — full row */}
          {hasCommission && (
            <FormField
              control={contractForm.control}
              name="tierOverride"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border bg-muted/30 px-4 py-3">
                  <div className="space-y-0.5">
                    <FormLabel className="text-sm font-medium">Sobrescrever Tier?</FormLabel>
                    <FormDescription className="text-xs">
                      Trava o creator no tier selecionado acima, ignorando progressão automática.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          )}

          {/* fixedValue — only when relevant */}
          {showFixedValue && (
            <FormField
              control={contractForm.control}
              name="fixedValue"
              render={({ field }) => (
                <FormItem className="sm:max-w-xs">
                  <FormLabel>Valor Fixo Mensal</FormLabel>
                  <FormControl>
                    <MoneyInput value={field.value ?? null} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </SettingsSection>
      </Form>

      {/* ── C: Redes Sociais ─────────────────────────────────────────── */}
      <Form {...socialForm}>
        <SettingsSection
          title="Redes Sociais"
          description="Usuários nas plataformas e dados de seguidores sincronizados"
          onSubmit={onSaveSocial}
          isDirty={socialForm.formState.isDirty}
          saveState={socialSave}
        >
          {/* Instagram */}
          <div className="space-y-2">
            <FormField
              control={socialForm.control}
              name="instagramUsername"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    <Instagram className="h-3.5 w-3.5 text-pink-500" />
                    Instagram
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        @
                      </span>
                      <Input className="pl-7" placeholder="usuario" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Read-only sync info */}
            <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              <span>
                Seguidores:{' '}
                <strong className="text-foreground">
                  {creator.instagramFollowers != null ? compact(creator.instagramFollowers) : '—'}
                </strong>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 px-2 text-xs"
                loading={isEnrichingInstagram}
                onClick={() => enrichInstagram(creator.id)}
              >
                <RefreshCw className="h-3 w-3" />
                Sincronizar agora
              </Button>
            </div>
          </div>

          <Separator />

          {/* TikTok */}
          <div className="space-y-2">
            <FormField
              control={socialForm.control}
              name="tiktokUsername"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    <span className="flex h-3.5 w-3.5 items-center justify-center rounded-sm bg-black text-[8px] font-bold text-white dark:bg-white dark:text-black">
                      TK
                    </span>
                    TikTok
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        @
                      </span>
                      <Input className="pl-7" placeholder="usuario" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex items-center rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              Seguidores:{' '}
              <strong className="ml-1 text-foreground">
                {creator.tiktokFollowers != null ? compact(creator.tiktokFollowers) : '—'}
              </strong>
            </div>
          </div>
        </SettingsSection>
      </Form>

      {/* ── D: Cupom & Links ─────────────────────────────────────────── */}
      <Form {...couponForm}>
        <SettingsSection
          id="coupon-section"
          title="Cupom & Links"
          description="Código de cupom, programa de desconto e link público"
          onSubmit={onSaveCoupon}
          isDirty={couponForm.formState.isDirty}
          saveState={couponSave}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={couponForm.control}
              name="couponCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Código do Cupom</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="CREATOR20"
                      className="font-mono tracking-wider"
                      {...field}
                      onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Apenas maiúsculas, números, hífens e underlines. 3–30 caracteres.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={couponForm.control}
              name="programId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Programa</FormLabel>
                  <Select
                    value={field.value ?? ''}
                    onValueChange={(v) => field.onChange(v || null)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um programa…" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {programs?.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} (
                          {p.rewardType === 'percentage'
                            ? `${p.rewardValue}%`
                            : `R$${p.rewardValue}`}
                          )
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription className="text-xs">
                    Define o desconto do cupom nas plataformas.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Sync status */}
          {creator.couponSyncStatus && creator.couponSyncStatus !== 'none' && (
            <div className="flex items-center gap-2">
              <Badge
                variant={creator.couponSyncStatus === 'synced' ? 'default' : 'destructive'}
                className="text-xs"
              >
                {creator.couponSyncStatus === 'synced' ? 'Sincronizado' : 'Erro'}
              </Badge>
              {creator.couponSyncError && (
                <span className="text-xs text-destructive">{creator.couponSyncError}</span>
              )}
              {creator.lastCouponSyncAt && (
                <span className="text-xs text-muted-foreground">
                  Último sync: {new Date(creator.lastCouponSyncAt).toLocaleString('pt-BR')}
                </span>
              )}
            </div>
          )}

          {/* External IDs (read-only) */}
          {(creator.guruCouponId || creator.yampiCouponId) && (
            <div className="flex flex-wrap gap-3">
              {creator.guruCouponId && (
                <div className="text-xs text-muted-foreground">
                  Guru ID: <span className="font-mono">{creator.guruCouponId}</span>
                </div>
              )}
              {creator.yampiCouponId && (
                <div className="text-xs text-muted-foreground">
                  Yampi ID: <span className="font-mono">{creator.yampiCouponId}</span>
                </div>
              )}
            </div>
          )}

          {/* Coupon action buttons */}
          {creator.couponCode && creator.programId && (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                loading={syncCoupon.isPending}
                onClick={() => syncCoupon.mutate(creator.id)}
              >
                <RefreshCw className="mr-1.5 h-3 w-3" />
                Sincronizar
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                loading={linkCoupon.isPending}
                onClick={() => linkCoupon.mutate({ creatorId: creator.id, syncToProgram: false })}
              >
                <ExternalLink className="mr-1.5 h-3 w-3" />
                Vincular Existente
              </Button>
              {(creator.guruCouponId || creator.yampiCouponId) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs text-destructive hover:text-destructive"
                  loading={deactivateCoupon.isPending}
                  onClick={() => deactivateCoupon.mutate(creator.id)}
                >
                  <PauseCircle className="mr-1.5 h-3 w-3" />
                  Desativar Cupom
                </Button>
              )}
            </div>
          )}

          {/* Read-only public page row */}
          {creator.publicToken && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Página Pública</Label>
              <div className="flex items-center gap-1">
                <div className="flex flex-1 items-center gap-1 overflow-hidden rounded-md border bg-muted/50 px-3 py-2">
                  <span className="flex-1 truncate text-xs text-muted-foreground">
                    {publicPageUrl}
                  </span>
                  <InlineCopyButton value={publicPageUrl} label="Copiar link" />
                </div>
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        asChild
                      >
                        <a href={publicPageUrl} target="_blank" rel="noreferrer">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Abrir página pública</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-[11px] text-muted-foreground/70">Token público não é editável.</p>
            </div>
          )}
        </SettingsSection>
      </Form>

      {/* ── E: Pagamento PIX ─────────────────────────────────────────── */}
      <Form {...pixForm}>
        <SettingsSection
          title="Pagamento PIX"
          description="Chave PIX para repasse de comissões"
          onSubmit={onSavePix}
          isDirty={pixForm.formState.isDirty}
          saveState={pixSave}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={pixForm.control}
              name="pixKeyType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Chave</FormLabel>
                  <Select
                    value={field.value ?? ''}
                    onValueChange={(v) =>
                      field.onChange((v || null) as z.infer<typeof pixSchema>['pixKeyType'])
                    }
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo…" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="cpf">CPF</SelectItem>
                      <SelectItem value="cnpj">CNPJ</SelectItem>
                      <SelectItem value="email">E-mail</SelectItem>
                      <SelectItem value="phone">Telefone</SelectItem>
                      <SelectItem value="random">Chave Aleatória</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={pixForm.control}
              name="pixKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Chave PIX</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={
                        pixKeyType
                          ? pixKeyTypePlaceholders[pixKeyType]
                          : 'Selecione o tipo primeiro'
                      }
                      disabled={!pixKeyType}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </SettingsSection>
      </Form>

      {/* ── F: Endereço para Envio ───────────────────────────────────── */}
      <Form {...addressForm}>
        <SettingsSection
          title="Endereço para Envio"
          description="Endereço utilizado para seedings e envios de produtos"
          onSubmit={onSaveAddress}
          isDirty={addressForm.formState.isDirty}
          saveState={addressSave}
        >
          <AddressFormFields form={addressForm} />
        </SettingsSection>
      </Form>

      {/* ── G: Tags ──────────────────────────────────────────────────── */}
      <Card className="shadow-none">
        <CardHeader className="flex flex-row items-start justify-between gap-4 px-5 pb-3 pt-5">
          <div className="space-y-0.5">
            <CardTitle className="text-sm font-semibold">Tags</CardTitle>
            <CardDescription className="text-xs">
              Categorize o creator com palavras-chave. Salvo automaticamente.
            </CardDescription>
          </div>
          {isSavingTags && (
            <Loader2 className="mt-0.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
        </CardHeader>
        <Separator />
        <CardContent className="space-y-3 px-5 py-5">
          {/* Tag pills */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="gap-1 pr-1.5 text-xs">
                  <Tag className="h-3 w-3 shrink-0 opacity-60" />
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-0.5 rounded-sm opacity-60 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                    aria-label={`Remover tag ${tag}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Input row */}
          <div className="flex gap-2">
            <Input
              placeholder="Nova tag (Enter para adicionar)"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-10 w-10 shrink-0"
              onClick={handleAddTag}
              disabled={!tagInput.trim() || isSavingTags}
              aria-label="Adicionar tag"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {tags.length === 0 && (
            <p className="text-xs text-muted-foreground/70">Nenhuma tag adicionada.</p>
          )}
        </CardContent>
      </Card>

      {/* ── H: Rastreamento & Verificação (read-only) ──────────────────── */}
      <Card className="shadow-none">
        <CardHeader className="px-5 pb-3 pt-5">
          <CardTitle className="text-sm font-semibold">Rastreamento & Verificação</CardTitle>
          <CardDescription className="text-xs">
            Dados de origem e verificação do cadastro (somente leitura)
          </CardDescription>
        </CardHeader>
        <Separator />
        <CardContent className="px-5 py-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Email verificado</Label>
              <p className="text-sm font-medium">
                {creator.emailVerifiedAt
                  ? `Sim — ${new Date(creator.emailVerifiedAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                  : 'Não verificado'}
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Origem (UTM Source)</Label>
              <p className="text-sm font-medium">{creator.utmSource || '—'}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Mídia (UTM Medium)</Label>
              <p className="text-sm font-medium">{creator.utmMedium || '—'}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Campanha (UTM Campaign)</Label>
              <p className="text-sm font-medium">{creator.utmCampaign || '—'}</p>
            </div>
            {creator.referrer && (
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs text-muted-foreground">Referrer</Label>
                <p className="text-sm font-medium truncate">{creator.referrer}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── I: Zona de Perigo ─────────────────────────────────────────── */}
      <Card className="border-l-4 border-l-destructive shadow-none">
        <CardHeader className="px-5 pb-3 pt-5">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold text-destructive">
            <AlertTriangle className="h-4 w-4" />
            Zona de Perigo
          </CardTitle>
          <CardDescription className="text-xs">
            Ações irreversíveis ou de alto impacto sobre o creator.
          </CardDescription>
        </CardHeader>
        <Separator />
        <CardContent className="px-5 py-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            {/* Status display */}
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">Status atual</p>
                <span
                  className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium ${
                    STATUS_COLORS[creator.status] ?? 'bg-muted text-muted-foreground'
                  }`}
                >
                  {STATUS_LABELS[creator.status] ?? creator.status}
                </span>
              </div>
              <p className="max-w-sm text-xs text-muted-foreground">
                {creator.status === 'active'
                  ? 'O creator está ativo e pode gerar vendas e comissões. Pausar irá suspender o rastreamento de novas vendas.'
                  : creator.status === 'paused'
                    ? 'O creator está pausado. Novos pedidos com o cupom dele não serão atribuídos.'
                    : creator.status === 'churned'
                      ? 'Este creator encerrou a parceria (churn). Reativar irá retomar o rastreamento.'
                      : 'Creator em fase de prospecção ou negociação.'}
              </p>
            </div>

            {/* Toggle action */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant={isActive ? 'destructive' : 'outline'}
                  size="sm"
                  loading={isDangerPending}
                  className="shrink-0 gap-1.5"
                >
                  {isActive ? (
                    <PauseCircle className="h-3.5 w-3.5" />
                  ) : (
                    <PlayCircle className="h-3.5 w-3.5" />
                  )}
                  {isActive ? 'Pausar Creator' : 'Ativar Creator'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    {isActive ? 'Pausar creator?' : 'Reativar creator?'}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {isActive ? (
                      <>
                        Ao pausar <strong>{creator.name}</strong>, novas vendas com o cupom{' '}
                        {creator.couponCode ? (
                          <strong className="font-mono">{creator.couponCode}</strong>
                        ) : (
                          'dele'
                        )}{' '}
                        não serão mais atribuídas a este creator. Esta ação pode ser desfeita a
                        qualquer momento.
                      </>
                    ) : (
                      <>
                        Ao reativar <strong>{creator.name}</strong>, o rastreamento de vendas e
                        comissões será retomado imediatamente.
                      </>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleToggleStatus}
                    className={
                      isActive
                        ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                        : ''
                    }
                  >
                    {isActive ? 'Sim, pausar' : 'Sim, reativar'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
